import { useMemo } from 'react'
import { useFirestoreGeneral } from '../../firebase/useFirestore'
import IndicatorData from '../graphData/IndicatorData'
import DashboardCardOptions from '../dashboard/DashboardCardOptions'
import DashboardPersonaFilter from '../dashboard/DashboardPersonaFilter'
import DashboardFilterBar from '../dashboard/DashboardFilterBar'
import usePersonaResponseCounts from '../../hooks/usePersonaResponseCounts'
import useIndicatorCardControls from '../../hooks/useIndicatorCardControls'
import { resolveWidgetGraphType } from '../../helpers/visualisationDefaults'
import LoadingSpinner from '../common/LoadingSpinner'
import filterTemplateEffectsResponses from '../../helpers/liveReportHelpers/filterTemplateEffectsResponses'

// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// Shared visualisation engine for a single Indicator (QuestionnaireField).
// Used by `IndicatorCardContent` on the dashboard and `QuestionWidget`
// in the live-report editor.
//
// templateData: when set, the indicator doc is resolved from the
// pre-fetched template blob instead of Firestore, and the same blob is
// forwarded to IndicatorData / useIndicatorCardControls so the chart
// data + moment-count lookup come from the template tenant.

/**
 * @param {object} props
 * @param {string} props.indicatorId - QuestionnaireField ID
 * @param {string} [props.effectId] - Parent effect (passes through to IndicatorData)
 * @param {string} [props.graphType]
 * @param {string[]} [props.colorPalette]
 * @param {Object<string, string>} [props.colorOverrides]
 * @param {'positive-first'|'negative-first'|''} [props.scaleColors] - Kleur de antwoorden van een keuzevraag als schaal
 * @param {Array<object>} [props.personas]
 * @param {boolean} [props.personasEnabled]
 * @param {string} [props.selectedPersonaId]
 * @param {(personaId: string) => void} [props.onPersonaChange]
 * @param {object|null} [props.dashboardItem]
 * @param {object|null} [props.templateData]
 * @param {Date|null} [props.startDate] - Period lower bound for the live data layer
 * @param {Date|null} [props.endDate] - Period upper bound (end of day) for the live data layer
 * @param {{start: string|null, end: string|null}|null} [props.periodRange] - Resolved period as ISO dates; filters template-blob rows client-side
 * @param {boolean} [props.hideControls] - Suppress this component's own chrome (persona filter + options footer) so a wrapping ComparisonFrame can own them once. See docs/features/compare-two-periods.md
 * @param {(payload: {kind: string, eligible: boolean, value: number|null}) => void} [props.onCompareData] - Reports this period's KPI value for a compare view (see docs/features/compare-two-periods.md)
 * @returns {JSX.Element}
 */
const IndicatorVisualization = ({
    indicatorId,
    effectId,
    graphType,
    graphColor,
    colorPalette,
    colorOverrides,
    scaleColors,
    personas,
    personasEnabled,
    selectedPersonaId,
    onPersonaChange,
    dashboardItem = null,
    templateData = null,
    startDate = null,
    endDate = null,
    periodRange = null,
    hideControls = false,
    onCompareData,
}) => {
    const isTemplate = !!templateData

    // Template mode: period-filter the blob's response rows once (see
    // filterTemplateEffectsResponses). Live mode filters via startDate/endDate.
    const periodTemplateData = useMemo(
        () => filterTemplateEffectsResponses(templateData, periodRange),
        [templateData, periodRange]
    )

    const liveResults = useFirestoreGeneral('QuestionnaireFields', 'ID', isTemplate ? '' : (indicatorId || ''))
    const indicator = isTemplate
        ? (templateData?.data?.effects?.indicators || []).find(i => i.ID === indicatorId)
        : (Array.isArray(liveResults) && liveResults.length > 0 ? liveResults[0] : null)

    const { resolvedFieldType, subItems } = useIndicatorCardControls(indicator, templateData)

    const showPersonaFilter = Boolean(personasEnabled) && Array.isArray(personas) && personas.length > 0
    const personaFieldIds = useMemo(
        () => (indicator?.ID ? [indicator.ID] : []),
        [indicator?.ID]
    )
    const personaResponseCounts = usePersonaResponseCounts({
        personas: showPersonaFilter ? personas : null,
        fieldIds: showPersonaFilter ? personaFieldIds : null,
    })

    if (!indicator) return <LoadingSpinner />

    // Een grafiektype dat bij dit vraagtype niet bestaat (bijv. 'Verdeling' op
    // een keuzevraag) valt terug op de standaard in plaats van niets te tekenen.
    const resolvedGraphType = resolveWidgetGraphType(resolvedFieldType, graphType)

    return (
        <>
            {!hideControls && showPersonaFilter && (
                <DashboardFilterBar
                    onClear={() => onPersonaChange && onPersonaChange('')}
                    activeFilterCount={selectedPersonaId ? 1 : 0}
                >
                    <DashboardPersonaFilter
                        personas={personas}
                        selectedPersonaId={selectedPersonaId || ''}
                        onChange={onPersonaChange}
                        responseCounts={personaResponseCounts}
                    />
                </DashboardFilterBar>
            )}
            <IndicatorData
                effectId={effectId}
                indicator={indicator}
                docid={indicator.docid}
                graphType={resolvedGraphType}
                graphColor={graphColor}
                personaId={selectedPersonaId || null}
                colorPalette={colorPalette}
                colorOverrides={colorOverrides}
                scaleColors={scaleColors}
                startDate={startDate}
                endDate={endDate}
                templateData={periodTemplateData}
                onCompareData={onCompareData}
                dashboardOptions={(!hideControls && dashboardItem) ? (
                    <DashboardCardOptions
                        item={dashboardItem}
                        fieldType={resolvedFieldType}
                        currentGraphType={resolvedGraphType}
                        subItems={subItems}
                    />
                ) : null}
            />
        </>
    )
}

export default IndicatorVisualization
